import { useState, useEffect, useCallback, useRef } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import jwt_decode from 'jsonwebtoken/decode';
import { getApiUrl } from '../utils/api';
import ReactNativeBridge from '../utils/reactNativeBridge';
import { getUserInfo, getAlarmStatus } from '../utils/apiCache';

// userId가 없을 때 localStorage JWT에서 동기 추출 (useEffect 대기 없이 즉시 사용)
const getTokenUserIdSync = () => {
    try {
        const token = localStorage.getItem('token');
        if (!token) return null;
        const decoded = jwt_decode(token);
        return decoded?.user_id || null;
    } catch {
        return null;
    }
};

const WELLNESS_CONFIG = {
  API_KEY: 'kko-a1z2-xv39-82yh0-dqlo',
  FRIEND_CACHE_TIMEOUT: 300000, // 5분 캐시 (재방문 시 즉시 표시)
  CRACK_ANIMATION: {
    MAX_CRACK_NUMBER: 15,
    INTERVAL_DELAY: 180, // 0.18초
    POPUP_TRIGGER_CRACK: 13
  },
  ALARM_CHECK_INTERVALS: {
    INITIAL_DELAY: 1000, // 3초 → 1초로 단축 (빠른 로딩)
    REPEAT_INTERVAL: 60000, // 60초 (15초 → 60초로 증가)
    FRIENDS_SYNC: 60000 // 60초 (30초 → 60초로 증가)
  }
};

// 전역 캐시 시스템
const friendStatusCache = new Map();
const friendCacheTimeout = WELLNESS_CONFIG.FRIEND_CACHE_TIMEOUT;

// 캐시된 친구 상태 확인 (훅 외부로 이동)
const getCachedFriendStatus = (userId) => {
    const cached = friendStatusCache.get(userId);
    if (cached && Date.now() - cached.timestamp < friendCacheTimeout) {
        return cached.data;
    }
    return null;
};

// 친구 상태 캐시에 저장 (훅 외부로 이동)
const setCachedFriendStatus = (userId, data) => {
    friendStatusCache.set(userId, {
        data,
        timestamp: Date.now()
    });
};

export const useWellness = (userId, userDataFromParent = null, isReturningFromDailyEarning = false, skipInitialAPICall = false) => {
    // userId가 null이면 렌더 시점에 즉시 JWT decode로 추출 (useEffect 대기 사이클 제거)
    const effectiveUserId = userId || getTokenUserIdSync();

    // StudyCafeV3와 동일한 패턴: 단순한 상태 관리
    const [friendStatus, setFriendStatus] = useState(() => {
        // 🚀 LCP 최적화: 캐시가 있으면 캐시 사용, 없으면 빈 배열
        const cached = getCachedFriendStatus(effectiveUserId);
        return cached || [];
    });
    const [userData, setUserData] = useState(userDataFromParent);
    const [showPopup, setShowPopup] = useState(false);
    const [receiverNickname, setReceiverNickname] = useState('');
    const [isButtonMoving] = useState(false);
    // myAlarmFromApp 상태 제거 - 새로운 API로 대체 예정
    const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
    const [isFriendDataLoaded, setIsFriendDataLoaded] = useState(() => {
        const cached = getCachedFriendStatus(effectiveUserId);
        return !!cached;
    });

    const [friendAddPromptTimer, setFriendAddPromptTimer] = useState(null);
    const [showFriendInviteTutorial, setShowFriendInviteTutorial] = useState(false);
    const [isAPICallComplete, setIsAPICallComplete] = useState(() => {
        const cached = getCachedFriendStatus(effectiveUserId);
        return !!cached;
    });
    const hasShownPopupRef = useRef(false); // 팝업이 한 번 표시되었는지 추적
    const fetchInProgressRef = useRef(false); // fetchFriendStatus in-flight 중복 방지

    // 부모로부터 받은 사용자 데이터가 있으면 사용
    useEffect(() => {
        if (userDataFromParent) {
            setUserData(userDataFromParent);
        }
    }, [userDataFromParent]);


    // 사용자 정보 조회 함수 - 부모에서 받은 데이터가 있으면 사용
    const fetchUserInfo = useCallback(async () => {
        // 부모에서 받은 사용자 데이터가 있으면 그대로 사용
        if (userDataFromParent) {
            return userDataFromParent;
        }

        // 캐시된 사용자 데이터가 있으면 사용
        if (userData) {
            return userData;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) return null;

            // 통합 캐시를 사용한 사용자 정보 조회
            const data = await getUserInfo(effectiveUserId, token);
            setUserData(data);
            return data;
        } catch (error) {
            console.error('사용자 정보 조회 에러:', error);
            return null;
        }
    }, [effectiveUserId, userDataFromParent, userData]);

    // 친구 상태 조회 함수 - 캐시 활용 (성능 최적화)
    const fetchFriendStatus = useCallback(async (currentUserData = null) => {
        // in-flight 중인 요청 있으면 무시 (이중 호출 방지)
        if (fetchInProgressRef.current) return;

        // 캐시 히트 시 즉시 반환
        const cachedFriendStatus = getCachedFriendStatus(effectiveUserId);
        if (cachedFriendStatus) {
            setFriendStatus(cachedFriendStatus);
            setIsInitialDataLoaded(true);
            setIsFriendDataLoaded(true);
            setIsAPICallComplete(true);
            return;
        }

        fetchInProgressRef.current = true;
        try {
            const response = await fetch(`${getApiUrl()}/kkoworld/friend/status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ requester_user_id: effectiveUserId }),
            });

            if (!response.ok) throw new Error('친구 상태 조회 실패');
            const data = await response.json();

            // 데이터가 변경된 경우에만 상태 업데이트 (깜빡임 방지)
            const cachedData = getCachedFriendStatus(effectiveUserId);
            if (JSON.stringify(cachedData) !== JSON.stringify(data) || !cachedData) {
                setFriendStatus(data);
            }

            setCachedFriendStatus(effectiveUserId, data);
            setIsInitialDataLoaded(true);
            setIsFriendDataLoaded(true);
            setIsAPICallComplete(true);
        } catch (e) {
            console.error('친구 상태 조회 에러:', e);
            setFriendStatus([]);
            setIsInitialDataLoaded(true);
            setIsFriendDataLoaded(true);
            setIsAPICallComplete(true);
        } finally {
            fetchInProgressRef.current = false;
        }
    }, [effectiveUserId]);


    // 계산된 값들 - 친구 데이터가 완전히 로드된 후에만 정확한 값 반환
    const realFriends = friendStatus.filter(friend => friend && friend.friend_name);
    const friendCount = realFriends.length;

    // V3와 동일한 패턴: 단순하고 안정적인 친구 판단 로직
    const hasFriends = (() => {
        // 친구 데이터가 로드되지 않았으면 false (로딩 중)
        if (!isFriendDataLoaded) return false;

        // friendStatus가 비어있으면 false (친구 없음)
        if (!friendStatus || friendStatus.length === 0) return false;

        // 실제 친구가 1명 이상 있으면 true
        return friendStatus.length > 0;
    })();

    // 친구 추가 크랙 애니메이션 로직 - 비활성화 (배포 최적화)
    useEffect(() => {
        // 크랙 애니메이션 비활성화 - 친구가 없을 때 바로 팝업 표시
        if (!effectiveUserId) {
            return;
        }

        if (isAPICallComplete && isFriendDataLoaded && (friendCount === 0 || friendStatus.length === 0) && !isReturningFromDailyEarning && !hasShownPopupRef.current) {
            setShowFriendInviteTutorial(true);
            hasShownPopupRef.current = true;
        } else if (isAPICallComplete && isFriendDataLoaded && !hasShownPopupRef.current) {
            setShowFriendInviteTutorial(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [friendCount, friendStatus.length, effectiveUserId, isReturningFromDailyEarning, isFriendDataLoaded, isAPICallComplete]); 


    // 친구 추가 함수 (외부에서 호출)
    const onAddFriend = useCallback(async (nickname) => {
        if (!nickname) {
            alert('친구 코드를 입력해주세요.');
            return;
        }

        amplitude.track('friend_add_attempt', {
            user_id: effectiveUserId,
            receiver_nickname: nickname
        });

        try {
            const response = await fetch(`${getApiUrl()}/kkoworld/friend/request`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'api_key': WELLNESS_CONFIG.API_KEY
                },
                body: JSON.stringify({
                    requester_user_id: effectiveUserId,
                    receiver_user_nickname: nickname
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                alert(data.message || '친구 추가 중 오류 발생');
                return;
            }

            amplitude.track('friend_add_success', {
                user_id: effectiveUserId,
                receiver_nickname: nickname
            });

            alert('친구 추가가 완료됐어요!');

            setShowFriendInviteTutorial(false);
            if (friendAddPromptTimer) {
                clearInterval(friendAddPromptTimer);
                setFriendAddPromptTimer(null);
            }

            friendStatusCache.delete(effectiveUserId);
            await fetchFriendStatus();
        } catch (e) {
            console.error(e);
            alert('네트워크 오류가 발생했습니다.');
        }
    }, [effectiveUserId, fetchFriendStatus, friendAddPromptTimer]);

    // 친구 삭제 함수
    const onDeleteFriend = useCallback(async (friendUserId) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);

        try {
            const response = await fetch(`${getApiUrl()}/kkoworld/friend/remove`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'api_key': WELLNESS_CONFIG.API_KEY
                },
                body: JSON.stringify({
                    requester_user_id: effectiveUserId,
                    friend_user_id: friendUserId
                }),
                signal: controller.signal
            });
            clearTimeout(timeoutId);

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 404) {
                    alert('이미 친구 관계가 아닙니다.');
                } else {
                    alert(data.message || '친구 삭제 중 오류가 발생했습니다.');
                }
                return false;
            }

            amplitude.track('friend_delete_success', { user_id: effectiveUserId, friend_user_id: friendUserId });

            friendStatusCache.delete(effectiveUserId);
            await fetchFriendStatus();
            return true;
        } catch (e) {
            clearTimeout(timeoutId);
            if (e.name === 'AbortError') {
                alert('요청 시간이 초과되었습니다. 다시 시도해주세요.');
            } else {
                console.error('친구 삭제 에러:', e);
                alert('네트워크 오류가 발생했습니다.');
            }
            return false;
        }
    }, [effectiveUserId, fetchFriendStatus]);

    // 팝업 열기
    const handlePlusClick = () => {
        setShowPopup(true);
        amplitude.track('friend_add_popup_opened', { user_id: effectiveUserId });
    };




    // 앱에서 오는 알람 상태 메시지 수신 - 새로운 API로 대체 예정
    useEffect(() => {
        const handleAppMessage = (event) => {
            try {
                const message = JSON.parse(event.data);

                if (message.type === 'ALARM_STATUS_RESPONSE') {
                    // 새로운 API가 준비되면 여기서 처리
                    // setMyAlarmFromApp(message.data.alarm_start_time);
                }
            } catch (error) {
                // 메시지 파싱 에러는 무시
            }
        };

        if (ReactNativeBridge.isReactNativeApp()) {
            window.addEventListener('message', handleAppMessage);
            document.addEventListener('message', handleAppMessage);

            return () => {
                window.removeEventListener('message', handleAppMessage);
                document.removeEventListener('message', handleAppMessage);
            };
        }
    }, []);

    // 알람 상태 관리
    const [hasAlarm, setHasAlarm] = useState(false);
    const [isStudying] = useState(false);

    // 알람 상태 확인 API 호출 함수 (통합 캐시 사용)
    const checkAlarmStatus = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            // 통합 캐시를 사용한 알람 상태 조회 (토큰 사용)
            const data = await getAlarmStatus(token);

            // 진행 중인 알람이 있으면 hasAlarm을 true로 설정
            if (data.isActive) {
                setHasAlarm(true);
            } else {
                setHasAlarm(false);
            }
        } catch (error) {
            console.error('알람 상태 확인 에러:', error);
            setHasAlarm(false);
        }
    }, []);
    
    // 🚨 최신 함수 참조 유지를 위한 ref
    const checkAlarmStatusRef = useRef(checkAlarmStatus);
    const fetchFriendStatusRef = useRef(fetchFriendStatus);
    
    useEffect(() => {
        checkAlarmStatusRef.current = checkAlarmStatus;
        fetchFriendStatusRef.current = fetchFriendStatus;
    }, [checkAlarmStatus, fetchFriendStatus]);

    // 🚨 초기 로딩 플래그 - userId별로 한 번만 실행되도록 보장
    const hasInitializedRef = useRef(new Set());
    
    // StudyCafeV3와 동일한 패턴: 컴포넌트 마운트 시 즉시 데이터 로드
    // 🚀 skipInitialAPICall이 true이면 초기 API 호출을 스킵 (WellnessScreen에서 triggerInitialAPIs로 처리)
    useEffect(() => {
        if (!effectiveUserId) return;

        const token = localStorage.getItem('token');
        if (!token) return;

        // skipInitialAPICall: true 브랜치 (interval만 설정, 초기 호출은 외부에서)
        if (skipInitialAPICall) {
            const { REPEAT_INTERVAL } = WELLNESS_CONFIG.ALARM_CHECK_INTERVALS;
            const checkStatus = async () => {
                if (document.hidden) return;
                await Promise.all([
                    checkAlarmStatusRef.current(),
                    (async () => {
                        if (!getCachedFriendStatus(effectiveUserId)) {
                            await fetchFriendStatusRef.current();
                        }
                    })()
                ]);
            };
            const statusIntervalId = setInterval(checkStatus, REPEAT_INTERVAL);
            checkStatus();
            return () => { clearInterval(statusIntervalId); };
        }

        // 중복 실행 방지 (userId별로 1회)
        if (hasInitializedRef.current.has(effectiveUserId)) return;
        hasInitializedRef.current.add(effectiveUserId);

        // 계정 전환 시 이전 userId 정리
        if (hasInitializedRef.current.size > 1) {
            Array.from(hasInitializedRef.current)
                .filter(id => id !== effectiveUserId)
                .forEach(id => hasInitializedRef.current.delete(id));
        }

        // 초기 API 병렬 호출
        const currentUserDataFromParent = userDataFromParent;
        const currentFetchUserInfo = fetchUserInfo;
        const currentFetchFriendStatus = fetchFriendStatus;
        const currentCheckAlarmStatus = checkAlarmStatus;

        if (currentUserDataFromParent) {
            Promise.all([
                currentFetchFriendStatus(currentUserDataFromParent),
                currentCheckAlarmStatus()
            ]).catch(error => { console.error('API 병렬 호출 에러:', error); });
        } else {
            Promise.all([
                currentFetchUserInfo(),
                currentFetchFriendStatus(),
                currentCheckAlarmStatus()
            ]).catch(error => { console.error('API 병렬 호출 에러:', error); });
        }

        // 60초 주기 갱신 interval
        const { REPEAT_INTERVAL } = WELLNESS_CONFIG.ALARM_CHECK_INTERVALS;
        const checkStatus = async () => {
            if (document.hidden) return;
            await Promise.all([
                checkAlarmStatusRef.current(),
                (async () => {
                    if (!getCachedFriendStatus(effectiveUserId)) {
                        await fetchFriendStatusRef.current();
                    }
                })()
            ]);
        };
        const statusIntervalId = setInterval(checkStatus, REPEAT_INTERVAL);

        return () => { clearInterval(statusIntervalId); };
    // effectiveUserId가 변경될 때만 재실행 (계정 전환 시)
    }, [effectiveUserId]);

    // receiverNickname 변경 핸들러
    const onReceiverNicknameChange = useCallback((value) => {
        setReceiverNickname(value);
    }, []);

    // 🚀 초기 API 호출을 수동으로 트리거할 수 있는 함수 export (병렬화용)
    const triggerInitialAPIs = useCallback(async () => {
        const currentUserDataFromParent = userDataFromParent;
        const currentFetchUserInfo = fetchUserInfo;
        const currentFetchFriendStatus = fetchFriendStatus;
        const currentCheckAlarmStatus = checkAlarmStatus;
        
        let result;
        if (currentUserDataFromParent) {
            // 친구 상태와 알람 상태를 병렬로 실행
            // fetchFriendStatus 내부에서 이미 setIsAPICallComplete(true)를 호출함
            result = await Promise.all([
                currentFetchFriendStatus(currentUserDataFromParent),
                currentCheckAlarmStatus()
            ]);
        } else {
            // 사용자 정보, 친구 상태, 알람 상태를 모두 병렬로 실행
            // fetchFriendStatus 내부에서 이미 setIsAPICallComplete(true)를 호출함
            result = await Promise.all([
                currentFetchUserInfo(),
                currentFetchFriendStatus(),
                currentCheckAlarmStatus()
            ]);
        }
        
        // 모든 API 호출 완료 후 플래그 설정 (fetchFriendStatus가 이미 설정했지만, 확실히 하기 위해)
        setIsAPICallComplete(true);
        
        return result;
    }, [userDataFromParent, fetchUserInfo, fetchFriendStatus, checkAlarmStatus]);

    return {
        friendStatus,
        userData,
        showPopup,
        receiverNickname,
        isButtonMoving,
        realFriends,
        friendCount,
        hasFriends,
        hasAlarm,
        isStudying,
        isInitialDataLoaded,
        isFriendDataLoaded, // 추가: 친구 데이터 로딩 상태
        setShowPopup,
        setReceiverNickname,
        handlePlusClick,
        onReceiverNicknameChange,
        onAddFriend, // 추가: 외부에서 호출하는 친구 추가 함수
        checkAlarmStatus, // 추가: 알람 상태 확인 함수
        showFriendInviteTutorial, // 친구 초대 튜토리얼 팝업 상태
        setShowFriendInviteTutorial, // 친구 초대 튜토리얼 팝업 제어
        triggerInitialAPIs, // 🚀 수동 트리거 함수 export (병렬화용)
        onDeleteFriend,
    };
};